// ---------------------------------------------------------------------------
// Estratto VERBATIM da TabContratti.tsx (Task #3874 — split interno Fase 2).
// Nessuna modifica logica: JSX spostato tale e quale, stato e handler restano
// nel componente padre e arrivano via props esplicite.
// ---------------------------------------------------------------------------
import type { Dispatch, SetStateAction } from 'react';
import { CreditCard } from 'lucide-react';
import type { InstallmentPlanRow } from '../types';
// Task #3875 — DTO risposta condivisi (alias per minimizzare il churn).
import type { CrmMasterPaymentPlan as PaymentPlanDetail } from '@easyflow/shared';
import { ModalWrapper } from '../shared';

interface PpDetailModalProps {
  ppDetail: PaymentPlanDetail | null;
  setPpDetail: Dispatch<SetStateAction<PaymentPlanDetail | null>>;
}

export function PpDetailModal({ ppDetail, setPpDetail }: PpDetailModalProps) {
  return (
      <ModalWrapper open={!!ppDetail} onClose={() => setPpDetail(null)} title={`Piano Rateale — ${ppDetail?.tenant_name || ''}`} wide>
        {ppDetail && (() => {
          let plan: InstallmentPlanRow[] = [];
          try { let raw = typeof ppDetail.installment_plan === 'string' ? JSON.parse(ppDetail.installment_plan) : (ppDetail.installment_plan || []); if (raw && !Array.isArray(raw) && raw.installments) raw = raw.installments; plan = Array.isArray(raw) ? raw : []; } catch { plan = []; }
          return (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-muted dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-xs">Stato</p>
                  <p className={`font-semibold ${ppDetail.status === 'accepted' ? 'text-green-400' : ppDetail.status === 'refused' ? 'text-red-400' : 'text-yellow-400'}`}>
                    {ppDetail.status === 'accepted' ? 'Accettato' : ppDetail.status === 'refused' ? 'Rifiutato' : 'In attesa'}
                  </p>
                </div>
                <div className="bg-muted dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-xs">Totale</p>
                  <p className="text-foreground dark:text-white font-semibold">€ {Number(ppDetail.total_amount || 0).toFixed(2)}</p>
                </div>
                <div className="bg-muted dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-xs">Durata</p>
                  <p className="text-foreground dark:text-white font-semibold">{ppDetail.duration_months} mesi ({ppDetail.frequency})</p>
                </div>
                <div className="bg-muted dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-xs">Metodo Pagamento</p>
                  <p className="text-foreground dark:text-white font-semibold flex items-center gap-1">
                    {ppDetail.stripe_pm_brand || ppDetail.stripe_pm_last4 ? (
                      <><CreditCard className="h-4 w-4 text-blue-400" /> {ppDetail.stripe_pm_brand || ''} {ppDetail.stripe_pm_last4 ? `••••${ppDetail.stripe_pm_last4}` : ''}</>
                    ) : ppDetail.stripe_customer_id ? (
                      <><CreditCard className="h-4 w-4 text-green-400" /> Configurato</>
                    ) : 'Non configurato'}
                  </p>
                </div>
              </div>

              {ppDetail.status === 'refused' && ppDetail.refused_reason && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-red-600 dark:text-red-400 text-sm font-medium">Motivo rifiuto:</p>
                  <p className="text-red-700 dark:text-red-300 text-sm mt-1">{ppDetail.refused_reason}</p>
                  {ppDetail.refused_at && <p className="text-red-400 text-xs mt-1">{new Date(ppDetail.refused_at).toLocaleString('it-IT')}</p>}
                </div>
              )}

              {ppDetail.status === 'accepted' && ppDetail.accepted_at && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <p className="text-green-600 dark:text-green-400 text-sm">Accettato il {new Date(ppDetail.accepted_at).toLocaleString('it-IT')}</p>
                </div>
              )}

              {Array.isArray(plan) && plan.length > 0 && (
                <div className="border border-border dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="bg-muted dark:bg-gray-700 px-4 py-2 text-sm text-muted-foreground dark:text-muted-foreground/60 font-medium">Dettaglio Rate</div>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border dark:border-gray-700 text-xs text-muted-foreground dark:text-muted-foreground/70">
                        <th className="py-2 px-4 text-left">#</th>
                        <th className="py-2 px-4 text-left">Scadenza</th>
                        <th className="py-2 px-4 text-right">Importo</th>
                        <th className="py-2 px-4 text-left">Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.map((inst, i: number) => (
                        <tr key={i} className="border-b border-border dark:border-gray-700/50">
                          <td className="py-2 px-4 text-muted-foreground dark:text-muted-foreground/60 text-sm font-mono">{inst.number || i + 1}</td>
                          <td className="py-2 px-4 text-foreground dark:text-white text-sm">{inst.due_date ? new Date(inst.due_date).toLocaleDateString('it-IT') : '-'}</td>
                          <td className="py-2 px-4 text-foreground dark:text-white text-sm text-right font-semibold">€ {Number(inst.amount || 0).toFixed(2)}</td>
                          <td className="py-2 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm">{inst.description || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {ppDetail.contract_number && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-blue-600 dark:text-blue-400 text-sm">Contratto associato: <span className="font-mono font-semibold">{ppDetail.contract_number}</span> — Stato: {ppDetail.contract_status}</p>
                </div>
              )}
            </div>
          );
        })()}
      </ModalWrapper>
  );
}
